import React, { useContext, useState } from "react";
import { useCookies } from "react-cookie";
import styled, { ThemeContext } from "styled-components";
import ReactHtmlParser from "react-html-parser";
import { Modal } from "notes";
import { RegistrationForm } from "./Components";
import { Layout } from "../Layout";
import analytics from "services/analytics";
import { fonts } from "theme/fonts";
import { Duration } from "luxon";
import {
  TimeContext,
  PoweredBy,
  CountdownSmall as Countdown,
} from "Components";
import playIcon from "Images/play-button.png";

export const Register = React.memo(({ event, onSuccess, ...props }) => {
  const themeContext = useContext(ThemeContext);
  const { time } = useContext(TimeContext);
  const [cookies, setCookie] = useCookies();

  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({});
  const [video, setVideo] = useState(null);

  let RegFormContent;

  const api = process.env.REACT_APP_FIRE_BASE_STORAGE_API;
  const storageDirectory = event.storageDirectory;

  let desktopImage = `${api}/o/${event?.register?.artistImageDesktop?.path}?alt=media`;
  let tabletImage = `${api}/o/${storageDirectory}%2F${event?.register?.artistImage?.tablet?.path}?alt=media`;
  let mobileImage = `${api}/o/${event?.register?.artistImageMobile?.path}?alt=media`;

  let headerLogo = `${api}/o/${event?.logos?.header?.path}?alt=media`;
  let footerLogo = `${api}/o/${event?.logos?.footer?.path}?alt=media`;
  let headerBg = `${api}/o/${storageDirectory}%2F${event?.register?.header?.background}?alt=media`;

  const {
    videoLink: ctaVideoLink,
    heading: ctaHeading,
    link: ctaLink,
    button: ctaButton,
  } = event.register.callToActions.primary;

  const {
    title: poweredByTitle,
    dealer: poweredByDealer,
  } = event.register.poweredBy;

  const handleSuccess = (admin, token, formPart, email) => {
    onSuccess(admin, token, formPart);
    if (formPart === 1) {
      setCookie("email", email, { maxAge: 31622400 });
      setFormData({ email });
    }
  };

  const handleFormPart3 = () => {
    const formPart = cookies.formPart;
    onSuccess(false, cookies.token, formPart);
    analytics.fireEvent({
      category: `Form Submit Part ${formPart}`,
      action: "Success",
      label: window.location.path,
    });
  };

  const setModalVisible = (bool, video) => {
    setVideo(video);
    setOpen(bool);
    if (bool) {
      analytics.fireEvent({
        category: "Video",
        action: "Hero Video",
        label: video,
      });
    } else {
      analytics.fireEvent({
        category: "Video",
        action: "Close Button",
        label: "Close Video",
      });
    }
  };

  if (event.campaignEnd < time) {
    RegFormContent = (
      <>
        <Heading>{ReactHtmlParser(event.postShow.closedHeader)}</Heading>
        <p>{ReactHtmlParser(event.postShow.pastBody)}</p>
      </>
    );
  } else {
    if (cookies?.formPart === "4") {
      const { time } = useContext(TimeContext);
      const { countdownHeading } = event.register.content.part4;

      const timeToStart =
        time < event.start &&
        Duration.fromMillis(event.start - time).shiftTo(
          "days",
          "hours",
          "minutes",
          "seconds",
          "milliseconds"
        );

      const timer = {
        label: countdownHeading,
        days: timeToStart.days,
        hours: timeToStart.hours,
        minutes: timeToStart.minutes,
        seconds: timeToStart.seconds,
      };

      RegFormContent = (
        <>
          <Countdown {...timer} />
          <PoweredBy title={poweredByTitle} dealer={poweredByDealer} />
        </>
      );
    } else if (cookies?.formPart === "3") {
      const {
        subHeading,
        submitButton,
        submitLink,
      } = event.register.content.part3;

      RegFormContent = (
        <>
          {subHeading && <SubHeading>{subHeading}</SubHeading>}
          <RegistrationCompleteButton
            href={submitLink}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleFormPart3}
          >
            {submitButton}
          </RegistrationCompleteButton>
          <PoweredBy title={poweredByTitle} dealer={poweredByDealer} />
        </>
      );
    } else if (cookies?.formPart === "2") {
      const {
        heading,
        subHeading,
        disclaimer,
        submitButton,
      } = event.register.content.part2;

      RegFormContent = (
        <>
          {heading && <Heading>{ReactHtmlParser(heading)}</Heading>}
          {subHeading && <SubHeading>{subHeading}</SubHeading>}
          <RegistrationForm
            formPart={2}
            formData={event}
            formLegal={disclaimer}
            onSuccess={handleSuccess}
            submitButton={submitButton}
          />
          <PoweredBy title={poweredByTitle} dealer={poweredByDealer} />
        </>
      );
    } else {
      const {
        heading,
        subHeading,
        submitButton,
      } = event.register.content.part1;

      RegFormContent = (
        <>
          {heading && <Heading>{ReactHtmlParser(heading)}</Heading>}
          {subHeading && <SubHeading>{subHeading}</SubHeading>}
          <RegistrationForm
            formPart={1}
            formData={event}
            onSuccess={handleSuccess}
            submitButton={submitButton}
          />
        </>
      );
    }
  }

  return (
    <Layout
      // header
      headerTitles={event.register.header.title}
      headerShowLogo={false}
      headerLogo={headerLogo}
      headerBgImage={headerBg}
      headerRegistration={true}
      headerCTA={event.register.callToActions.secondary}
      setModalVisible={setModalVisible}
      // hero
      heroContent={false}
      // heading
      // main
      backgroundColor={"#102b4e"}
      // add-ons
      noAddons
      // footer
      footerRegistration={true}
      footerShowText={true}
      footerShowLogo={true}
      footerLogo={footerLogo}
      footerLogoLink={event.logos.footer.link}
      footerText={event?.register?.footer}
      footerPrivacyPolicy={event?.footer}
    >
      <Registration>
        <Content as="section" id="registration-content">
          <ContentHeader>
            <ContentHeaderContainer>
              {themeContext.register.altLogo?.active && (
                <AltLogoLink
                  href={event.logos.header.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => {
                    analytics.fireEvent({
                      category: "External Link",
                      action: "Registration Alt Logo",
                      label: event.logos.header.link,
                    });
                  }}
                >
                  <AltLogo src={headerLogo} />
                </AltLogoLink>
              )}
            </ContentHeaderContainer>
          </ContentHeader>

          <ContentContainer>{RegFormContent}</ContentContainer>
        </Content>

        <ArtistImage
          as="section"
          id="artist-image"
          desktopImage={desktopImage}
          tabletImage={tabletImage}
          mobileImage={mobileImage}
        >
          {themeContext.register.overlayImage.active && (
            <OverlayContainer>
              <ModalButton onClick={() => setModalVisible(true, ctaVideoLink)}>
                <OverlayImage src={playIcon} />
              </ModalButton>
              <OverlayTitle>{ctaHeading}</OverlayTitle>
              <OverlayButtonContainer>
                <OverlayButton
                  href={ctaLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => {
                    analytics.fireEvent({
                      category: "External Link",
                      action: ctaButton,
                      label: ctaLink,
                    });
                  }}
                >
                  {ctaButton}
                </OverlayButton>
              </OverlayButtonContainer>
            </OverlayContainer>
          )}
        </ArtistImage>
      </Registration>

      <VideoModal size="large" open={open} setOpen={setModalVisible}>
        <VideoModalContainer>
          <Video
            src={video}
            frameborder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowfullscreen
          />
        </VideoModalContainer>
      </VideoModal>
    </Layout>
  );
});

// ======== REGISTRATION CONTAINER ======== //

const Registration = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;

  @media only screen and ${(props) => props.theme.maxMedia.medium} {
    flex-direction: row;
  }
`;

// ======== CONTENT / LEFT SIDE ======== //

const Content = styled.div`
  background: #ffffff;
  display: flex;
  flex-direction: column;
  color: #102b4e;
  margin-bottom: 15px;
  box-shadow: 0 3px 6px 0px rgb(0 0 0 / 20%);
  z-index: 10;
  overflow: hidden;

  ::before,
  ::after {
    content: "";
    height: 40px;
    width: 40px;
    background: #102b4e;
    margin: 0 auto;
    display: flex;
  }

  ::before {
    margin-top: -20px;
    border-bottom-right-radius: 50%;
    border-bottom-left-radius: 50%;
  }

  ::after {
    margin-bottom: -20px;
    border-top-right-radius: 50%;
    border-top-left-radius: 50%;
  }

  @media only screen and ${(props) => props.theme.maxMedia.medium} {
    width: 450px;
    margin-bottom: 0;
    margin-top: -145px;
  }
`;

const ContentHeader = styled.div`
  border-bottom: 1px solid rgba(16, 43, 78, 0.5);
`;

const ContentHeaderContainer = styled.div`
  display: flex;
  padding: 25px 15px;
  max-width: 650px;
  margin: 0 auto;
  align-items: end;
  justify-content: center;

  @media only screen and ${(props) => props.theme.maxMedia.medium} {
    padding: 8px 35px;
    max-width: 450px;
  }
`;

const ContentContainer = styled.div`
  margin: 0 auto;
  padding: 25px 15px 50px;
  width: 100%;
  height: 100%;
  max-width: 650px;
  flex: 1;

  @media only screen and ${(props) => props.theme.maxMedia.medium} {
    max-width: unset;
    padding: 24px;
    max-width: 450px;
    min-height: 650px;
  }
`;

const AltLogoLink = styled.a`
  width: 100%;
  max-width: 225px;
`;

const AltLogo = styled.img`
  width: 100%;
`;

const Heading = styled.h2`
  font-family: ${fonts.brand.bold};
  font-size: 18px;
  line-height: 20px;
  margin-bottom: 16px;
  text-transform: uppercase;
  color: #4580dd;
`;

const SubHeading = styled.h3`
  font-family: ${fonts.brand.medium};
  font-size: 18px;
  line-height: 22px;
  margin-bottom: 8px;
`;

const Body = styled.p`
  font-family: ${fonts.brand.regular};
  order: 3;
`;

const SweepstakesRulesLink = styled.a`
  font-size: 12px;
  color: #626466;
  text-transform: uppercase;
`;

const RegistrationCompleteButton = styled.a`
  margin: 16px 0 0 0;
  background-color: #4580dd;
  color: #ffffff;
  font-family: ${fonts.brandLight};
  transition: all ease 0.3s;
  padding: 25px;
  display: flex;
  justify-content: center;

  text-decoration: none;

  &:hover,
  &:focus {
    background-color: #305da2;
  }

  @media only screen and ${(props) => props.theme.maxMedia.small} {
    font-size: 26px;
  }
`;

// ======== ARTIST IMAGE / RIGHT SIDE ======== //

const ArtistImage = styled.div`
  align-items: flex-start;
  background-color: #000000;
  background-image: url(${(props) => props.mobileImage});
  background-repeat: no-repeat;
  background-size: cover;
  padding-top: ${(9 / 16) * 100}%;
  position: relative;
  background-position: center;

  @media only screen and ${(props) => props.theme.maxMedia.medium} {
    background-image: url(${(props) => props.tabletImage});
    background-position: 48%;
    flex-grow: 1;
    padding-top: 0;
  }

  @media only screen and ${(props) => props.theme.maxMedia.large} {
    background-image: url(${(props) => props.desktopImage});
    background-position: 70%;
  }
`;

const OverlayContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100%;

  @media only screen and ${(props) => props.theme.maxMedia.medium} {
    padding: 15px;
    height: unset;
    top: unset;
    bottom: 75px;
    width: clamp(400px, 39vw, 475px);
    margin: 0 auto;
  }

  @media only screen and ${(props) => props.theme.maxMedia.large} {
    width: clamp(450px, 30vw, 475px);
    right: 90px;
    margin: unset;
    left: unset;
  }
`;

const OverlayImage = styled.img`
  width: clamp(50px, 15vw, 100px);
  margin-bottom: 10px;
`;

const OverlayTitle = styled.h3`
  color: #ffffff;
  text-transform: uppercase;
  font-family: ${fonts.header};
  font-size: 7vw;
  margin-bottom: 10px;
  font-family: ${fonts.brandCondensedBold};
  text-align: center;
  line-height: 1.25;

  @media only screen and ${(props) => props.theme.maxMedia.medium} {
    font-size: clamp(36px, 5vw, 52px);
  }
`;

const OverlayButtonContainer = styled.div`
  width: 100%;
  padding: 0 15px;
  position: absolute;
  bottom: -35px;
  @media only screen and ${(props) => props.theme.maxMedia.medium} {
    position: relative;
    bottom: unset;
    padding: 0;
  }
`;

const OverlayButton = styled.a`
  font-family: ${fonts.brandLight};
  color: #ffffff;
  background: #4580dd;
  padding: 25px;
  display: flex;
  justify-content: center;
  text-decoration: none;
  line-height: 1.15;
  @media only screen and ${(props) => props.theme.maxMedia.small} {
    font-size: 24px;
  }
`;

// ======== MODAL ======== //

const ModalButton = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  outline: none;
  border: 0;
  background-color: transparent;

  :hover {
    text-decoration: none;
  }
`;

const VideoModal = styled(Modal)`
  justify-content: center;
  min-height: unset;
  position: relative;

  svg {
    top: 10px;
    right: 10px;
  }
`;

const VideoModalContainer = styled.div`
  padding-top: 56.25%;
  position: relative;
`;

const Video = styled.iframe`
  height: 100%;
  position: absolute;
  top: 0;
  width: 100%;
  border: 0;
`;
